import type { ComponentGroup, Form, FormComponent, GoogleReview, GoogleReviews, Section, Step } from "types";

/**
 * Logique métier du bloc « avis Google », extraite dans un composable pur pour
 * être testable en isolation et partagée entre le store (fetch/normalisation)
 * et GoogleReviewsComponent (formatage). Aucune dépendance à Vue/Nuxt ici.
 */

/** Note affichée par défaut quand aucune note n'est fournie (sites de marque). */
export const GOOGLE_REVIEWS_FALLBACK_RATING = 4.5;

/** `steps` est sérialisé en objet à clés numériques par les configs, pas en tableau. */
function asArray<T> (collection: unknown): Array<T> {
  if (Array.isArray(collection)) { return collection as Array<T>; }
  if (collection && typeof collection === "object") {
    return Object.values(collection as Record<string, T>);
  }
  return [];
}

/**
 * Détecte si une config utilise le bloc avis Google, soit via le flag
 * top-level `form.googleReviews` (stepper desktop), soit via un composant de
 * type "googleReviews" dans le flux (mobile/les_furets). Parcours ciblé du
 * schéma plutôt qu'une recherche de sous-chaîne sur le form sérialisé.
 */
export function usesGoogleReviews (form: Form | null | undefined): boolean {
  if (!form) { return false; }
  if (form.googleReviews === true) { return true; }
  return asArray<Step>(form.steps).some(step =>
    asArray<Section>(step?.sections).some(section =>
      asArray<ComponentGroup>(section?.component_groups).some(group =>
        asArray<FormComponent>(group?.components).some(component => component?.component === "googleReviews")
      )
    )
  );
}

/**
 * Visibilité du bloc dans le stepper desktop : hors mobile, flag config ET
 * donnée fetchée. Source unique du bloc et des hauteurs (conteneur + étapes),
 * qui doivent rester alignés.
 */
export function shouldShowGoogleReviewsDesktop (
  form: Form | null | undefined,
  reviews: GoogleReviews | null | undefined,
  isMobile = false
): boolean {
  return !isMobile && !!(form && form.googleReviews && reviews);
}

/** Vrai si `value` est un nombre fini exploitable. */
function isFiniteNumber (value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

/** Valide la forme d'un avis individuel renvoyé par le backend. */
function isValidReview (review: unknown): review is GoogleReview {
  if (!review || typeof review !== "object") { return false; }
  const r = review as Record<string, unknown>;
  return typeof r.author === "string" &&
    typeof r.text === "string" &&
    isFiniteNumber(r.rating);
}

/**
 * Reconstruit un avis à sa forme exacte : on ne propage que les champs connus
 * (pas de fuite de propriétés inconnues du payload) et `date` uniquement si
 * c'est bien une chaîne (sinon un `date` numérique/objet s'afficherait tel quel).
 */
function toGoogleReview (review: GoogleReview): GoogleReview {
  const clean: GoogleReview = {
    author: review.author,
    rating: review.rating,
    text: review.text
  };
  if (typeof review.date === "string") { clean.date = review.date; }
  return clean;
}

/**
 * Normalise/valide le payload brut de GET /google-reviews/:site. Renvoie `null`
 * dès que la note est inexploitable (endpoint 404, payload vide/malformé, note
 * absente ou <= 0) → le bloc se masque proprement. La frontière de confiance
 * avec le backend est explicite ici plutôt qu'éparpillée dans les composants.
 */
export function normalizeGoogleReviews (data: unknown): GoogleReviews | null {
  if (!data || typeof data !== "object") { return null; }
  const d = data as Record<string, unknown>;
  // Une note doit être un nombre strictement positif : 0 = pas de note
  // exploitable (le fallback 4.5 est géré côté backend, cf. performs!68).
  if (!isFiniteNumber(d.rating) || d.rating <= 0) { return null; }
  return {
    rating: d.rating,
    url: sanitizeReviewsUrl(d.url),
    // Cap défensif à 5 : c'est la limite dure de l'API Google (cf. contrat
    // backend) ; on ne fait pas confiance aveuglément à la taille du payload.
    reviews: Array.isArray(d.reviews)
      ? (d.reviews.filter(isValidReview) as Array<GoogleReview>).slice(0, 5).map(toGoogleReview)
      : []
  };
}

/**
 * N'autorise qu'un lien http(s) absolu pour la fiche Google ; tout le reste
 * (schémas `javascript:`, valeurs non-string) est neutralisé en `null`.
 */
export function sanitizeReviewsUrl (url: unknown): string | null {
  if (typeof url !== "string") { return null; }
  return /^https?:\/\//i.test(url.trim()) ? url.trim() : null;
}

/** Borne une note dans l'intervalle [0, 5]. */
export function clampRating (value: number): number {
  const safe = isFiniteNumber(value) ? value : GOOGLE_REVIEWS_FALLBACK_RATING;
  return Math.min(5, Math.max(0, safe));
}

/** Largeur (%) de la barre d'étoiles pleines pour une note donnée. */
export function ratingToPercent (value: number): string {
  return `${(clampRating(value) / 5) * 100}%`;
}

/** Formate une note en français avec une décimale (4.5 → "4,5"). */
export function formatRating (value: number): string {
  return new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  }).format(clampRating(value));
}
